// ─── src/services/universalSearch.js ─────────────────────────────────────────
// LWD Universal Search, client for the server-side `universal_search` RPC.
//
// Server-side, typo-tolerant, ranked, federated across listings (every vendor
// category), regions (destinations) and magazine_posts (guides + editorial).
// This is STANDARD business search, NOT Aura (the natural-language pipeline).
//
//   const { results, grouped } = await universalSearch("villa nascosta");
//   result.route → hand to routeSearchResult() to navigate.
// ─────────────────────────────────────────────────────────────────────────────
import { supabase } from "../lib/supabaseClient";

// Friendly label per listing category (mirrors the entity shown on result cards)
const CATEGORY_LABEL = {
  "wedding-venues": "Venue",
  planners: "Planner",
  photographers: "Photographer",
  videographers: "Videographer",
  florists: "Florist",
  "bridal-boutiques": "Boutique",
  caterers: "Caterer",
  cakes: "Cake Designer",
  entertainment: "Entertainment",
  stationery: "Stationery",
  jewellery: "Jeweller",
  transport: "Transport",
  celebrants: "Celebrant",
  "hair-makeup": "Hair & Makeup",
  "event-design": "Event Design",
};

const titleCase = (s = "") =>
  s.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// squished normalization, mirrors lwd_norm() server-side, for the query log
export const normalizeQuery = (s = "") =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");

function labelFor(row) {
  if (row.entity === "destination") return "Destination";
  if (row.entity === "atlas") return "Wedding Atlas";
  if (row.entity === "publication") return "Publication";
  if (row.entity === "real_wedding") return "Real Wedding";
  if (row.entity === "guide") return "Guide";
  if (row.entity === "editorial") return "Editorial";
  return CATEGORY_LABEL[row.category_slug] || (row.listing_type ? titleCase(row.listing_type) : "Listing");
}

// One meaningful "intelligence" chip per venue, what separates LWD from a directory.
function venueChips(row) {
  const chips = [];
  if (row.exclusive_use) chips.push("Exclusive use");
  const cap = parseInt(row.capacity, 10);
  if (cap > 0) chips.push(`${cap} guests`);
  return chips.slice(0, 2);
}

// The second line on a result card. Venues lead with place + an intelligence chip
// ("Lake Como · Exclusive use · 120 guests"); other entities keep label · place.
function subtitleFor(row) {
  const label = labelFor(row);
  if (row.entity === "destination") return "Destination";
  if (row.entity === "atlas") return ["Planning guide", titleCase(row.country || "")].filter(Boolean).join(" · ");
  if (row.entity === "publication") {
    const n = parseInt(row.capacity, 10); // RPC carries featured-venue count here
    return n > 0 ? `Publication · ${n} featured venues` : "Publication";
  }
  if (row.entity === "real_wedding") return row.city || "Real Wedding"; // location stored in city
  if (row.entity === "guide" || row.entity === "editorial") return labelFor(row);
  if (row.category_slug === "wedding-venues") {
    const place = row.region || row.city || titleCase(row.country || "");
    return [place, ...venueChips(row)].filter(Boolean).join(" · ") || label;
  }
  const place = [row.city || row.region, titleCase(row.country || "")].filter(Boolean).join(", ");
  return place ? `${label} · ${place}` : label;
}

// A self-describing route the page can act on without re-deriving anything.
function routeFor(row) {
  if (row.entity === "listing")
    return { kind: "venue", payload: { slug: row.slug, countrySlug: row.country_slug, regionSlug: row.region_slug, categorySlug: row.category_slug } };
  if (row.entity === "destination")
    return { kind: "destination", countrySlug: row.country_slug || row.country, regionSlug: row.region_slug, href: row.route || null };
  if (row.entity === "atlas")
    return { kind: "atlas", slug: row.slug, href: row.route || `/${row.slug}` };
  if (row.entity === "publication")
    return { kind: "publication", slug: row.slug, href: row.route || `/publications/${row.slug}` };
  if (row.entity === "real_wedding")
    return { kind: "realwedding", slug: row.slug, href: row.route || `/real-weddings/${row.slug}` };
  // guide | editorial → The Edit
  return { kind: "edit", href: row.route || `/lwd-edit/${row.slug}`, slug: row.slug };
}

function mapRow(row, i) {
  return {
    ...row,
    rank: i,
    label: labelFor(row),
    subtitle: subtitleFor(row),
    route: routeFor(row),
  };
}

// Bucket flat ranked rows into display groups (order = display order).
const GROUP_ORDER = ["venues", "vendors", "destinations", "guides", "editorial"];
const GROUP_TITLE = {
  venues: "Venues",
  vendors: "Vendors",
  destinations: "Destinations",
  guides: "Guides",
  editorial: "The Edit",
};
function bucketOf(r) {
  if (r.entity === "destination") return "destinations";
  if (r.entity === "guide") return "guides";
  if (r.entity === "editorial") return "editorial";
  return r.category_slug === "wedding-venues" ? "venues" : "vendors";
}

export function groupResults(results) {
  const buckets = {};
  for (const r of results) (buckets[bucketOf(r)] ||= []).push(r);
  return GROUP_ORDER
    .filter((k) => buckets[k]?.length)
    .map((k) => ({ key: k, title: GROUP_TITLE[k], items: buckets[k] }));
}

// SERP grouping, 4 premium groups (Venues, Creatives, Destinations, Editorial).
// Kept separate from groupResults() so the (locked) homepage dropdown is untouched.
const SERP_ORDER = ["venues", "creatives", "destinations", "atlas", "publications", "realweddings", "editorial"];
const SERP_TITLE = { venues: "Venues", creatives: "Creatives", destinations: "Destinations", atlas: "Wedding Atlas", publications: "Publications", realweddings: "Real Weddings", editorial: "The Edit" };
function serpBucketOf(r) {
  if (r.entity === "destination") return "destinations";
  if (r.entity === "atlas") return "atlas";
  if (r.entity === "publication") return "publications";
  if (r.entity === "real_wedding") return "realweddings";
  if (r.entity === "guide" || r.entity === "editorial") return "editorial";
  return r.category_slug === "wedding-venues" ? "venues" : "creatives";
}
export function groupForSerp(results) {
  const buckets = {};
  for (const r of results) (buckets[serpBucketOf(r)] ||= []).push(r);
  return SERP_ORDER.filter((k) => buckets[k]?.length).map((k) => ({ key: k, title: SERP_TITLE[k], items: buckets[k] }));
}

// ── Universal Discovery, every entity is a first-class citizen ───────────────
// The dropdown is DISCOVERY (best few per type, intent-ordered); the /search
// page is exhaustive. These groups + the intent classifier power the dropdown.
const DISCOVERY_TITLE = {
  destinations: "Destinations", venues: "Venues", creatives: "Creatives",
  atlas: "Wedding Atlas", publications: "Publications", realweddings: "Real Weddings", editorial: "The Edit",
};
// Curate, don't dump, cap each section to its best few.
const DISCOVERY_CAP = { destinations: 2, venues: 4, creatives: 3, atlas: 3, publications: 2, realweddings: 2, editorial: 3 };

function discoveryBucketOf(r) {
  if (r.entity === "destination") return "destinations";
  if (r.entity === "atlas") return "atlas";
  if (r.entity === "publication") return "publications";
  if (r.entity === "real_wedding") return "realweddings";
  if (r.entity === "guide" || r.entity === "editorial") return "editorial";
  return r.category_slug === "wedding-venues" ? "venues" : "creatives";
}

// Deterministic intent, decides which sections lead. No AI in the hot path.
const RE_CATEGORY = /\b(photographer|photography|photo|videographer|video|film|cinemato|planner|planning|florist|flower|floral|cater|cake|celebrant|officiant|entertain|band|dj|musician|makeup|make-up|hair|stylist|stationery|invitation|jewell|jewel|transport|car|dress|gown|bridal|boutique|designer)/i;
const RE_KNOWLEDGE = /\b(how|getting married|get married|marry|cost|costs|price|pricing|budget|legal|requirement|document|visa|paperwork|permit|licen[cs]e|when|season|weather|month|guide|plan|planning|itinerary)\b/i;
const RE_VENUE = /\b(venue|villa|castle|castello|chateau|château|estate|hotel|palazzo|palace|manor|mansion|hall|barn|vineyard|winery|resort|farmhouse|masseria)/i;

const ORDER_DEFAULT  = ["destinations", "venues", "atlas", "publications", "realweddings", "editorial", "creatives"];
const ORDER_KNOWLEDGE = ["atlas", "destinations", "venues", "publications", "realweddings", "editorial", "creatives"];
const ORDER_CATEGORY = ["creatives", "venues", "destinations", "atlas", "publications", "realweddings", "editorial"];
const ORDER_VENUE    = ["venues", "destinations", "atlas", "publications", "realweddings", "editorial", "creatives"];

export function detectIntent(q = "") {
  if (RE_CATEGORY.test(q)) return "category";
  if (RE_VENUE.test(q)) return "venue";
  if (RE_KNOWLEDGE.test(q)) return "knowledge";
  return "default";
}
function intentOrder(q) {
  switch (detectIntent(q)) {
    case "category": return ORDER_CATEGORY;
    case "venue": return ORDER_VENUE;
    case "knowledge": return ORDER_KNOWLEDGE;
    default: return ORDER_DEFAULT;
  }
}

// Group ranked results into intent-ordered, curated discovery sections.
export function groupForDiscovery(results, q = "") {
  const buckets = {};
  for (const r of results) (buckets[discoveryBucketOf(r)] ||= []).push(r);
  return intentOrder(q)
    .filter((k) => buckets[k]?.length)
    .map((k) => ({ key: k, title: DISCOVERY_TITLE[k], items: buckets[k].slice(0, DISCOVERY_CAP[k]) }));
}

// ── Per-query cache + in-flight de-dupe (instant repeats, no flicker, no dups) ─
const _cache = new Map();
const _inflight = new Map();
const CACHE_MAX = 60;
function cacheGet(k) { if (_cache.has(k)) { const v = _cache.get(k); _cache.delete(k); _cache.set(k, v); return v; } return null; }
function cacheSet(k, v) { _cache.set(k, v); if (_cache.size > CACHE_MAX) _cache.delete(_cache.keys().next().value); }

/**
 * Run a universal search. Returns { results, grouped }. Cached per (query,limit);
 * concurrent identical calls share one request.
 */
export async function universalSearch(query, { limit = 24 } = {}) {
  const q = (query || "").trim();
  if (q.length < 2) return { results: [], grouped: [] };
  const key = `${q.toLowerCase()}|${limit}`;
  const cached = cacheGet(key);
  if (cached) return cached;
  if (_inflight.has(key)) return _inflight.get(key);
  const pr = (async () => {
    const { data, error } = await supabase.rpc("universal_search", { q, max_results: limit });
    _inflight.delete(key);
    if (error || !Array.isArray(data)) {
      if (error) console.warn("[universalSearch]", error.message);
      return { results: [], grouped: [] };
    }
    const results = data.map(mapRow).filter((r) => r.title && r.title.trim());
    const out = { results, grouped: groupResults(results) };
    cacheSet(key, out);
    return out;
  })();
  _inflight.set(key, pr);
  return pr;
}

/**
 * Recovery for thin / zero-result searches: did-you-mean + forward paths.
 * Returns { didYouMean, venues, destinations, editorial } (or null).
 */
export async function getRecovery(query, { limit = 6 } = {}) {
  const q = (query || "").trim();
  if (q.length < 2) return null;
  const { data, error } = await supabase.rpc("search_recovery", { q, lim: limit });
  if (error || !Array.isArray(data)) {
    if (error) console.debug("[getRecovery]", error.message);
    return null;
  }
  const rows = data.filter((r) => r.title && r.title.trim()).map((r, i) => {
    const row = { ...r, country: r.country || r.country_slug, rank: i };
    return { ...row, label: labelFor(row), subtitle: subtitleFor(row), route: routeFor(row) };
  });
  return {
    didYouMean: rows.filter((r) => r.kind === "did_you_mean"),
    venues: rows.filter((r) => r.kind === "venue"),
    destinations: rows.filter((r) => r.kind === "destination"),
    editorial: rows.filter((r) => r.kind === "editorial"),
  };
}

// ── Live "Popular searches" pills ────────────────────────────────────────────
// Real top searches from the query log, with a curated fallback so the homepage
// always reads intentionally (esp. at launch, before there's enough traffic).
export const CURATED_POPULAR = ["Lake Como", "Tuscany", "Amalfi Coast", "Photographers"];
const titleCasePill = (s) => (s || "").replace(/\s+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase());

export async function getPopularSearches({ limit = 4 } = {}) {
  try {
    const { data, error } = await supabase.rpc("popular_searches", { days: 30, lim: 16 });
    if (error || !Array.isArray(data)) return CURATED_POPULAR.slice(0, limit);
    const seen = new Set();
    const out = [];
    for (const r of data) {
      const t = titleCasePill(r.term);
      const k = t.toLowerCase();
      if (t.length < 3 || seen.has(k)) continue;
      seen.add(k);
      out.push(t);
      if (out.length >= limit) break;
    }
    // Pad with curated terms (not already shown) until we hit `limit`
    for (const c of CURATED_POPULAR) {
      if (out.length >= limit) break;
      if (!seen.has(c.toLowerCase())) { out.push(c); seen.add(c.toLowerCase()); }
    }
    return out.slice(0, limit);
  } catch {
    return CURATED_POPULAR.slice(0, limit);
  }
}

// ── Query intelligence (append-only log; best-effort, never blocks UX) ───────
function sessionId() {
  try {
    let id = localStorage.getItem("lwd_search_sid");
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("lwd_search_sid", id); }
    return id;
  } catch { return null; }
}

export function logSearch({ query, resultCount, source = "homepage" }) {
  const q = (query || "").trim();
  if (q.length < 2) return;
  supabase.from("search_queries").insert({
    event_type: "search",
    query: q,
    normalized: normalizeQuery(q),
    result_count: resultCount,
    zero_result: resultCount === 0,
    source,
    session_id: sessionId(),
  }).then(({ error }) => { if (error) console.debug("[logSearch]", error.message); });
}

export function logSearchClick({ query, result, source = "homepage" }) {
  const q = (query || "").trim();
  if (!result) return;
  supabase.from("search_queries").insert({
    event_type: "click",
    query: q,
    normalized: normalizeQuery(q),
    clicked_result_id: String(result.id),
    clicked_result_type: result.entity,
    clicked_rank: result.rank ?? null,
    source,
    session_id: sessionId(),
  }).then(({ error }) => { if (error) console.debug("[logSearchClick]", error.message); });
}
